/**
 * ShipAddressOptions.js — Saved shipping-address picker (kind
 * "checkout-address-options"). Common Commerce7 checkout step, shared by
 * every tenant using features/optional/checkout_c7.js (any flow).
 *
 * Parity with old CHECKOUT.cart.addressOptions (RedConcierge/static/javascript/
 * main.js ~5372): logged-in customers with saved addresses see the default
 * address pre-selected, a "Use another address" toggle for the rest, and
 * "+ Add new address" to fall through to the blank ship form. checkout_c7.js
 * only shows this step when the customer is logged in AND has at least one
 * saved address — otherwise it goes straight to the blank form, so this
 * component can assume `addresses` is non-empty.
 *
 * message.data: { addresses: [{ id, firstName, lastName, company, address,
 *   address2, city, stateCode, zipCode, countryCode, phone, isDefault }],
 *   events?: { select?, new?, edit? } }
 *
 * events overrides the default emitted names below — club_c7.js's club
 * checkout reuses this same component/look but passes its own event names
 * so it doesn't also fire checkout_c7.js's cart-checkout listeners (which
 * have no session guard of their own and would proceed the wrong — cart,
 * not club-signup — checkout).
 *
 * EVENTS OUT (defaults; see events above):
 *   ws-checkout-address-select { address }  — Continue with the picked address
 *   ws-checkout-address-new {}               — "Add new address" clicked
 *   ws-checkout-address-edit { address }     — pencil icon clicked
 */

import { emit } from "../../../core/state.js";

// Same icon as OrderReview's "Edit Address" (components/base/messages/
// OrderReview.js) — reused here so the affordance reads as one system.
const EDIT_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor"/><path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/></svg>';

function addressLines(addr) {
  const line1 = [addr.address, addr.address2].filter(Boolean).join(", ");
  const line2 = [addr.stateCode, addr.city].filter(Boolean).join(", ");
  const line3 = [addr.countryCode, addr.zipCode].filter(Boolean).join("-");
  return [line1, line2, line3].filter(Boolean);
}

export default class ShipAddressOptions {
  render(message) {
    const addresses = Array.isArray(message?.data?.addresses) ? message.data.addresses : [];
    const events = message?.data?.events || {};
    const selectEvent = events.select || "ws-checkout-address-select";
    const newEvent = events.new || "ws-checkout-address-new";
    const editEvent = events.edit || "ws-checkout-address-edit";
    const defaultAddr = addresses.find((a) => a.isDefault) || addresses[0];
    const others = addresses.filter((a) => a !== defaultAddr);

    let selected = defaultAddr;

    const form = document.createElement("form");
    form.className = "ws-checkout-form ws-ship-address-form";
    form.setAttribute("action", "javascript:void(0);");

    const sec = document.createElement("div");
    sec.className = "ws-checkout-section";
    const head = document.createElement("div");
    head.className = "ws-checkout-section-head";
    const title = document.createElement("p");
    title.className = "ws-checkout-section-title";
    title.textContent = "Shipping Address";
    head.appendChild(title);
    sec.appendChild(head);

    const renderCard = (addr, isDefaultCard) => {
      const card = document.createElement("label");
      card.className = "ws-ship-address-card";

      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "shipAddress";
      radio.className = "ws-ship-address-radio";
      radio.value = addr.id;
      radio.checked = addr === selected;
      radio.addEventListener("change", () => {
        selected = addr;
      });
      card.appendChild(radio);

      const info = document.createElement("div");
      info.className = "ws-ship-address-info";
      const name = document.createElement("p");
      name.className = "ws-ship-address-name";
      name.textContent = `${[addr.firstName, addr.lastName].filter(Boolean).join(" ")}${isDefaultCard ? " (Default)" : ""}`.trim();
      info.appendChild(name);
      addressLines(addr).forEach((line) => {
        const p = document.createElement("p");
        p.className = "ws-ship-address-line";
        p.textContent = line;
        info.appendChild(p);
      });
      if (addr.phone) {
        const phone = document.createElement("p");
        phone.className = "ws-ship-address-line";
        phone.textContent = `Phone: ${addr.phone}`;
        info.appendChild(phone);
      }
      card.appendChild(info);

      const editBtn = document.createElement("button");
      editBtn.type = "button";
      editBtn.className = "ws-ship-address-edit";
      editBtn.setAttribute("aria-label", "Edit address");
      editBtn.innerHTML = EDIT_ICON;
      // Labels forward plain clicks to their radio, but stop here too so a
      // click on the pencil never also toggles selection (old addressOptions'
      // "if(editActionBtn){ return; }" guard).
      editBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        emit(editEvent, { address: addr });
      });
      card.appendChild(editBtn);

      return card;
    };

    if (defaultAddr) sec.appendChild(renderCard(defaultAddr, true));

    if (others.length) {
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "ws-ship-address-toggle";
      toggle.innerHTML =
        '<span>Use another address</span><span class="ws-ship-address-chevron">›</span>';

      const otherList = document.createElement("div");
      otherList.className = "ws-ship-address-others";
      otherList.hidden = true;
      others.forEach((addr) => otherList.appendChild(renderCard(addr, false)));

      toggle.addEventListener("click", () => {
        otherList.hidden = !otherList.hidden;
        toggle.classList.toggle("is-open", !otherList.hidden);
      });

      sec.appendChild(toggle);
      sec.appendChild(otherList);
    }

    const addNew = document.createElement("button");
    addNew.type = "button";
    addNew.className = "ws-ship-address-add";
    addNew.innerHTML = '<span class="ws-ship-address-plus">+</span> Add new address';
    addNew.addEventListener("click", () => emit(newEvent, {}));
    sec.appendChild(addNew);

    // Footer lives inside the section so Continue sits within the same
    // rounded/shadowed card as the addresses above it (not a separate
    // full-width bar below the card).
    const footer = document.createElement("div");
    footer.className = "ws-checkout-footer";
    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "ws-checkout-submit";
    submit.textContent = "Continue...";
    submit.disabled = !selected;
    footer.appendChild(submit);
    sec.appendChild(footer);

    form.appendChild(sec);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (!selected) return;
      submit.disabled = true;
      emit(selectEvent, { address: selected });
    });

    return form;
  }
}
